import { Controller } from "@hotwired/stimulus";
import Papa from "papaparse";

export default class extends Controller {
  static targets = [
    "form",
    "importType", 
    "assetLayoutName", 
    "file", 
    "errors", 
    "preview", 
    "description",
    "assetLayoutNameGroup"
  ];
  
  static values = { 
    url: String,
    assetLayouts: Array 
  };

  connect() {
    this.csvData = [];
    this.csvMeta = [];
    this.ready = false;
    this.updateDescription();
  }

  importTypeChanged() {
    this.updateDescription();
    this.toggleAssetLayoutNameField();
  }

  updateDescription() {
    const importType = this.importTypeTarget.value;
    let description = "";
    
    switch(importType) {
      case "Companies":
        description = "Companies match on name. Don't import companies that will be synced through an integration.";
        break;
      case "Knowledge Base Articles":
        description = "Knowledge Base Articles do not match, they will always create a new Article.";
        break;
      case "Passwords":
        description = "Passwords do not match, they will always create a new Password.";
        break;
      case "BlankAssetLayout":
        description = "This will create a new Asset Layout then create new Assets in the Asset Layout. All field types will be text. Field types can be updated afterward in Admin/Asset Layouts. If the Asset's Company name is not present or a company with a matching name is not found, the Asset will not be created. Don't import assets that will be synced through an integration.";
        break;
      default:
        if (importType !== "Companies" && importType !== "Knowledge Base Articles" && importType !== "Passwords" && importType !== "BlankAssetLayout") {
          description = "This will create new Assets in an existing Asset Layout. If there are validation rules that are not met, the Asset will not insert. If the Asset's Company name is not present or a company with a matching name is not found, the Asset will not be created. Don't import assets that will be synced through an integration.";
        }
        break;
    }
    
    this.descriptionTarget.textContent = description;
  }

  toggleAssetLayoutNameField() {
    const isBlankAssetLayout = this.importTypeTarget.value === "BlankAssetLayout";
    
    if (this.hasAssetLayoutNameGroupTarget) {
      this.assetLayoutNameGroupTarget.style.display = isBlankAssetLayout ? 'block' : 'none';
      this.assetLayoutNameTarget.required = isBlankAssetLayout;
    }
  }

  filesChange(event) {
    const files = event.target.files;
    if (!files.length) return;

    const file = files[0];
    
    Papa.parse(file, {
      delimiter: "",
      newline: "",
      quoteChar: '"',
      escapeChar: '"',
      header: true,
      transformHeader: undefined,
      dynamicTyping: false,
      preview: 0,
      encoding: "",
      worker: false,
      comments: false,
      step: undefined,
      complete: (results, file) => {
        this.csvData = results.data;
        this.csvMeta = results.meta;
        this.displayErrors(results.errors);
        this.ready = true;
        this.showPreview();
      },
      error: undefined,
      download: false,
      downloadRequestHeaders: undefined,
      skipEmptyLines: true,
      chunk: undefined,
      fastMode: undefined,
      beforeFirstChunk: undefined,
      withCredentials: undefined,
      transform: undefined,
      delimitersToGuess: [',', '\t', '|', ';', Papa.RECORD_SEP, Papa.UNIT_SEP]
    });
  }

  displayErrors(errors) {
    this.errorsTarget.innerHTML = '';
    
    if (errors && errors.length > 0) {
      const errorList = errors.map(error => 
        `<li>Row ${error.row}: ${error.message}</li>`
      ).join('');
      this.errorsTarget.innerHTML = errorList;
    }
  }

  showPreview() {
    if (!this.ready || !this.csvMeta.fields) return;

    const template = document.getElementById('upload-preview');
    if (!template) {
      console.error('Upload preview template not found');
      return;
    }

    const clone = template.content.cloneNode(true);

    const headerRow = clone.querySelector('thead tr');
    const tbody = clone.querySelector('tbody');

    if (!headerRow || !tbody) {
      console.error('Table structure not found in template');
      return;
    }

    this.csvMeta.fields.forEach(field => {
      const th = document.createElement('th');
      th.textContent = field;
      headerRow.appendChild(th);
    });

    this.csvData.forEach((datum, index) => {
      const tr = document.createElement('tr');
      
      const lineCell = document.createElement('td');
      lineCell.textContent = index + 2;
      tr.appendChild(lineCell);
      
      this.csvMeta.fields.forEach(field => {
        const td = document.createElement('td');
        td.textContent = datum[field] || '';
        tr.appendChild(td);
      });
      
      tbody.appendChild(tr);
    });

    this.previewTarget.innerHTML = '';
    this.previewTarget.appendChild(clone);
    this.previewTarget.style.display = 'block';
  }

  submitForm(event) {
    event.preventDefault();
    
    if (!this.formTarget.checkValidity()) {
      this.formTarget.reportValidity();
      return;
    }

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
    
    fetch(this.urlValue, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken
      },
      body: JSON.stringify({
        import: {
          import_type: this.importTypeTarget.value,
          data: this.csvData,
          asset_layout_name: this.assetLayoutNameTarget.value,
          matching: this.matching || {}
        }
      })
    })
    .then(response => {
      if (response.ok) {
        window.location.reload();
      } else {
        console.error('Import failed');
      }
    })
    .catch(error => {
      console.error('Error:', error);
    });
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}
